import {useState,useEffect} from 'react'
import {envAwarePost,capitalize} from '../../../util/util'

import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as faIcons  from '@fortawesome/free-solid-svg-icons'

import Select from 'react-select'

import { DndProvider,useDrag,useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import {
	useCreateMenuGroup,
	useRemoveMenuGroup,
	useEditMenuGroup,
	useArrangeMenu,
	useClientMenu,
	useResetMenu,
} from '../hooks'

import {pathsIdentical} from '../util'


/*--------------------------------------------

Spec:

Drag source and drop target paths are defined
as an array of integers.

Index 0: represents if src/tgt is in menu or disabled list
Index 1: represents the index within the menu or disabled list

If path has 3 entries
Index 2: represents the index within the group

Note: checking path length is important because it robustly 
handles edge cases like if the menu starts with a group. eg
dropPath = [0,0] means move to start of menu (even if first item is a group)
dropPath = [0,0,i] means move to the ith item in in the first menu group

--------------------------------------------*/

const GROUP = 'group'
const NAV_ITEM = 'navItem'

//icons in the icon object use camel case 
//variants of the lisp case that is used 
//everywhere else.
const lispToCamel = (s)=>(
	`fa${s.replace('fa-','').split('-').map(capitalize).join('')}`
)

const Spacer = () => <div style={{height:'8px'}} />

const NavItem = ({navItem})=>{
	const [{ isDragging }, drag] = useDrag(() => ({
		type: NAV_ITEM,
		item:navItem,
		collect: (monitor) => ({
			isDragging: Boolean(monitor.isDragging())
		})
	}))
	
	return (
		<div 
			ref={drag} 
			style={{
				opacity:isDragging?0.3:1,
				cursor:'grab'
			}}
			className="p-1 py-2 rounded border bg-white shadow d-flex align-items-center"
		>
			<i className="fa fa-grip-vertical mx-2 text-muted"/>
			<small>
				<FontAwesomeIcon 
					className="mr-1" 
					icon={faIcons[lispToCamel(navItem.icon)]}
				/>
				<strong>
					{navItem.name}
				</strong>
				<span className="mx-1">-</span>
				<span className="ml-1 text-muted">{navItem.description}</span>
			</small>
		</div>
	)
}

const GroupItem = ({navItem})=>{
	const [{ isDragging }, drag] = useDrag(() => ({
		type: NAV_ITEM,
		item:navItem,
		collect: (monitor) => ({
			isDragging: Boolean(monitor.isDragging())
		})
	}))
	
	return (
		<div 
			ref={drag} 
			style={{
				opacity:isDragging?0.3:1,
				cursor:'grab'
			}}
			className="mt-1 d-flex align-items-center"
		>
			<i className="fa fa-grip-vertical mx-2 text-muted"/>
			<small>
				<FontAwesomeIcon 
					className="mr-1" 
					icon={faIcons[lispToCamel(navItem.icon)]}
				/>
				<strong>{navItem.name}</strong>
				<span className="mx-1">-</span>
				<span className="ml-1 text-muted">{navItem.description}</span>
			</small>
		</div>
	)
}

const Group = ({group,handleEdit})=>{

	const remove = useRemoveMenuGroup()
	const [removing,setRemoving] = useState(false)
	const handleRemove = ()=>{
		if(!window.confirm('Are you sure you want to remove this menu group?')) return
		setRemoving(true)
		remove(group).then(()=>{
			setRemoving(false)
		})
	}

	const [{ isDragging }, drag] = useDrag(() => ({
		type: GROUP,
		item:group,
		collect: (monitor) => ({
			isDragging: Boolean(monitor.isDragging())
		})
	}))

	const [{canDrop,isOver}, drop] = useDrop(
		(monitor) => ({
			accept: NAV_ITEM,
			item:group,
			collect:(monitor)=>({
				isOver: monitor.isOver(),
				canDrop: monitor.canDrop()
			})
		}),
	)

	return (
		<div 
			ref={drag} 
			className="rounded border p-1 bg-white shadow" 
			style={{opacity:isDragging?0.3:1}}
		>
			<div ref={drop}>
				<div className="d-flex justify-content-between">
					<div style={{cursor:'grab'}}>
						<i className="fa fa-grip-vertical mx-2 text-muted"/>
						<small>
							<FontAwesomeIcon 
								className="mr-1" 
								icon={faIcons[lispToCamel(group.icon)]}
							/>
							<strong>{group.name}</strong>
							<span className="mx-1">-</span>
							<span className="ml-1 text-muted">{group.description}</span>
						</small>
					</div>
					<div>
						<OverlayTrigger trigger={["hover","focus"]}overlay={
							<Tooltip>
								This will remove the group and move all menu items to disabled
							</Tooltip>
						}>
							<Button 
								disabled={removing}
								onClick={handleRemove}
								variant="outline-danger"
								size="sm"
								className="mr-1"
							>
								{
									removing
									? <Spinner animation="border" size="sm"/>
									: <i className="fa fa-trash"/>
								}
							</Button>
						</OverlayTrigger>
						<Button 
							variant="outline-primary" 
							onClick={handleEdit.bind(null,group)}
							size="sm"
						>
							<FontAwesomeIcon icon={faIcons.faEdit}/>
						</Button>
					</div>
				</div>
				<hr className="my-1"/>
				<div className="pl-2">
					<DropPreview 
						accept={NAV_ITEM} 
						dropPath={[...group.path,0]}
					/>
					{group.nav_items.length>0&&group.nav_items.map((navItem,i)=>
						<>
							<GroupItem navItem={navItem}/>
							<DropPreview 
								accept={NAV_ITEM}
								dropPath={[...group.path,i+1]}
							/>
						</>
					)}
					{group.nav_items.length===0&&
						<>
							<small>Drag items here</small>
							<DropPreview 
								accept={NAV_ITEM}
								dropPath={[...group.path,0]}
							/>
						</>
					}
				</div>
			</div>
		</div>
	)
}

const DropPreview = ({accept,dropPath=[]})=>{
	const arrangeMenu = useArrangeMenu()
	const [{canDrop,isOver}, drop] = useDrop(
		monitor => ({
			accept,
			drop: item => {
				const tgtPath = dropPath
				const srcPath = item.path
				arrangeMenu({tgtPath,srcPath})
			},
			collect: monitor => {
				const item = monitor.getItem()
				const isDragging = Boolean(item)
				const itemPath = item?.path || []
				const samePath = pathsIdentical(dropPath,itemPath)
				const canDrop = monitor.canDrop()
				const droppingAfterSelf = (
					itemPath.length === dropPath.length 
					&& 
					dropPath.at(-1) === itemPath.at(-1) + 1
				)
				return ({
					isOver: monitor.isOver(),
					canDrop: (
						canDrop
						&&isDragging
						&&!samePath
						&&!droppingAfterSelf
					),
				})
			}
		}),
	)

	return (
		<div style={{
			height:isOver?'20px':'4px',
			width:'100%',
			transition:'0.1s',
			background:isOver?'rgba(0,0,0,0.2)':'transparent',
			position:'relative'
		}}>
			<div ref={drop} style={{
				display:canDrop?'block':'none',
				position:'absolute',
				height:isOver?'28px':'20px',
				top:0,
				left:0,
				transform:'translateY(-8px)',
				width:'100%'
			}}>
			</div>
		</div>
	)
}

const DisabledItems = ({children})=>{
	const arrangeMenu = useArrangeMenu()
	const [{canDrop,isOver}, drop] = useDrop(
		(monitor) => ({
			accept:[GROUP,NAV_ITEM],
			drop: item => {
				const tgtPath = [1,children.length]
				const srcPath = item.path
				if (pathsIdentical(tgtPath,srcPath)) return 
				arrangeMenu({tgtPath,srcPath})
			},
			collect:(monitor)=>({
				isOver: monitor.isOver(),
				canDrop: monitor.canDrop()
			})
		}),
	)
	return (
		<div className="w-100 ml-2 d-flex flex-column align-items-stretch">
			<div>
				<strong>Disabled Menu Items</strong>
			</div>
			<hr className="my-1 w-100"/>
			<div 
				ref={drop} 
				style={{
					background:isOver?'rgba(0,0,0,0.1)':'#fff',
					height:'100%',
					minHeight:'200px',
					minWidth:'200px',
					paddingBottom:'40px'
				}}
			>
				{children.length>0
					?children
					: (
						<div className="p-3 text-center">
							<strong>Drag & drop items here to disable them</strong>
						</div>
					)
				}
			</div>
		</div>
	)
}

const DashboardItem = ()=>(
	<div className="p-1 py-2 rounded border bg-white shadow pl-4">
		<FontAwesomeIcon icon={faIcons.faGauge} className="mr-1"/>
		Dashboard
		<small className="d-block text-muted">
			Dashboard cannot be edited and will always be the first item in the list
		</small>
	</div>
)

const RootLevel = ({children})=>{
	const [{canDrop,isOver}, drop] = useDrop(
		() => ({
			accept: [GROUP,NAV_ITEM],
			collect:(monitor)=>({
				isOver: monitor.isOver({shallow:true}),
				canDrop: monitor.canDrop()
			})
		}),
	)
	return (
		<div ref={drop}>
			{children}
		</div>
	)
}

const IconPicker = ({selectedIcon,onChange})=>{
	const [showOptions,setShowOptions] = useState(false)
	const icons = [
		'fa-calendar',
		'fa-calendar-days',
		'fa-calendar-day',
		'fa-calendar-week',
		'fa-calendar-check',
		'fa-burger',
		'fa-apple-whole',
		'fa-folder-open',
		'fa-folder',
		'fa-heart',
		'fa-dumbbell',
		'fa-clock',
		'fa-user',
		'fa-circle-user',
		'fa-users',
		'fa-list-ul',
		'fa-rectangle-list',
		'fa-heart',
		'fa-heart-pulse',
		'fa-person-walking',
		'fa-person-running',
		'fa-person-rays',
		'fa-person-biking',
		'fa-weight-scale',
		'fa-stopwatch',
		'fa-chart-line',
		'fa-table-cells',
		'fa-tv',
		'fa-image',
		'fa-video',
		'fa-file-video',
		'fa-inbox',
		'fa-utensils',
		'fa-gauge',
		'fa-comment',
		'fa-bell',
		'fa-book',
		'fa-bowl-food',
		'fa-file',
		'fa-toolbox',
		'fa-robot',
		'fa-list-check',
		'fa-clock',
		'fa-info-circle',
		'fa-question-circle',
		'fa-clipboard-check',
		'fa-door-open'
	]
	useEffect(()=>{
		const closeIconPicker = ()=>{
			setShowOptions(false)
		}
		window.addEventListener('click',closeIconPicker)
		return ()=>{
			window.removeEventListener('click',closeIconPicker)
		}
	},[])
	return (
		<div 
			className="border rounded text-center" 
			style={{
				position:'relative',
				height:'38px',
				lineHeight:'38px',
				cursor:'pointer'
			}}
			onClick={e=>{
				e.stopPropagation()
			}}
		>
			<div 
				className="d-flex justify-content-between align-items-center px-2" 
				style={{height:'100%'}}
				onClick={()=>{
					setShowOptions(p=>!p)
				}}
			>
				{Boolean(selectedIcon)&&
					<FontAwesomeIcon 
						icon={faIcons[lispToCamel(selectedIcon)]} 
					/>
				}
				{!Boolean(selectedIcon)&&
					'Select'
				}
				<FontAwesomeIcon className="ml-1" icon={faIcons.faCaretDown}  />
			</div>
			{showOptions&&
				<div 
					style={{
						position:'absolute',
						bottom:'-4px',
						right:0,
						transform:'translateY(100%)',
						width:'300px',
						zIndex:99999
					}}
					className="d-flex flex-wrap bg-white p-1 rounded border shadow"
				>
					{icons.map(icon=>(
						<div 
							onClick={onChange.bind(null,icon)}
							className="d-inline-flex align-items-center justify-content-center" 
							style={{
								width:'32px',
								height:'32px',
								borderRadius:'32px',
							}}
						>
							<FontAwesomeIcon 
								icon={faIcons[lispToCamel(icon)]} 
							/>
						</div>
					))}
				</div>
			}
		</div>
	)
}

const CreateGroupModal = ({show,onHide})=>{
	const createMenuGroup = useCreateMenuGroup()
	const [name,setName] = useState('')
	const [icon,setIcon] = useState('')
	const [description,setDescription] = useState('')
	const [pending,setPending] = useState(false)
	const icons = [
		'calendar',
		'calendar-days',
		'calendar-check',
		'burger',
		'apple-whole',
		'folder-open',
		'folder',
		'heart',
		'dumbbell',
		'clock',
		'user',
		'circle-user',
		'users',
		'list-ul',
		'rectangle-list',
	]

	const handleSave = ()=>{
		setPending(true)
		createMenuGroup({name,icon:icon,description}).then(()=>{
			setPending(false)
			setName('')
			setIcon('')
			setDescription('')
			onHide()
		})
	}
	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header>
				Create Menu Group
			</Modal.Header>
			<Modal.Body>
				<div className="d-flex">
					<div style={{flex:'1 1 auto'}} className="mr-1">
						<strong>Name</strong>
						<Form.Control 
							onChange={e=>{
								setName(e.target.value)
							}}
							placeholder="name"
							value={name}
						/>
					</div>
					<div style={{width:'110px',flex:'0 0 auto'}}>
						<strong>Icon</strong>
						<IconPicker
							onChange={setIcon}
							selectedIcon={icon}
						/>
					</div>
				</div>
				<strong>Description (optional)</strong>
				<Form.Control 
					onChange={e=>{
						setDescription(e.target.value)
					}}
					value={description}
					as="textarea"
				/>
			</Modal.Body>
			<Modal.Footer>
				<Button 
					disabled={pending}
					onClick={handleSave}
					variant="outline-success" 
					size="sm"
				>
					Save {pending&&<Spinner animation="border" size="sm"/>}
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

const EditGroupModal = ({group,show,onHide})=>{

	const createMenuGroup = useCreateMenuGroup()
	const [name,setName] = useState('')
	const [icon,setIcon] = useState('')
	const [description,setDescription] = useState('')
	const [pending,setPending] = useState(false)

	useEffect(()=>{
		if (!group) return 
		setName(group.name)
		setIcon(group.icon)
		setDescription(group.description)
	},[group])

	const editMenuGroup = useEditMenuGroup()

	const handleSave = ()=>{
		setPending(true)
		const id = group.id
		const update = {name,icon,description}
		editMenuGroup({id,update}).then(()=>{
			setPending(false)
			setName('')
			setIcon('')
			setDescription('')
			onHide()
		})
	}
	if(!group) return null
	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header>
				Edit Menu Group
			</Modal.Header>
			<Modal.Body>
				<div className="d-flex">
					<div style={{flex:'1 1 auto'}} className="mr-1">
						<strong>Name</strong>
						<Form.Control 
							onChange={e=>{
								setName(e.target.value)
							}}
							placeholder="name"
							value={name}
						/>
					</div>
					<div style={{width:'110px',flex:'0 0 auto'}}>
						<strong>Icon</strong>
						<IconPicker
							onChange={setIcon}
							selectedIcon={icon}
						/>
					</div>
				</div>
				<strong>Description (optional)</strong>
				<Form.Control 
					onChange={e=>{
						setDescription(e.target.value)
					}}
					value={description}
					as="textarea"
				/>
			</Modal.Body>
			<Modal.Footer>
				<Button 
					disabled={pending}
					onClick={handleSave}
					variant="outline-success" 
					size="sm"
				>
					Update {pending&&<Spinner animation="border" size="sm"/>}
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

export default function ClientAppCustomization () {
	const {status,enabled,disabled} = useClientMenu()


	const [showCreateGroupModal,setShowCreateGroupModal] = useState(false)
	const [editGroup,setEditGroup] = useState(null)
	const [showEditGroupModal,setShowEditGroupModal] = useState(false)

	const handleEditGroup = (group)=>{
		setEditGroup(group)
		setShowEditGroupModal(true)
	}

	const resetMenu = useResetMenu()
	const [resetting,setResetting] = useState(false)
	const handleReset = ()=>{
		if(!window.confirm('Are you sure you want to reset the menu?')) return
		setResetting(true)
		resetMenu().then(()=>{
			setResetting(false)
		})
	}
	
	return (
		<div>
			<div className="mb-2">
				<OverlayTrigger placement="bottom" overlay={
					<Tooltip>
						Create a custom group to organize menu items
					</Tooltip>
				}>
					<Button 
						onClick={setShowCreateGroupModal.bind(null,true)}
						variant="outline-success"
						size="sm"
					>
						Add Menu Group
					</Button>
				</OverlayTrigger>
				<OverlayTrigger placement="bottom" overlay={
					<Tooltip>
						Returns menu to the default configuration
					</Tooltip>
				}>
					<Button 
						disabled={resetting}
						onClick={handleReset}
						variant="outline-danger"
						size="sm"
						className="ml-2"
					>
						Reset Menu {resetting&&<Spinner animation="border" size="sm"/>}
					</Button>
				</OverlayTrigger>
			</div>
			<div className="my-2">
				<i className="fa fa-info-circle mr-1"/>
				Drag and drop items to customize what clients can see/use when they log in. Use this to simplify your client's experience.
			</div>
			<DndProvider backend={HTML5Backend}>
				<RootLevel>
					<div className="d-flex justify-content-between align-items-stretch">
						<div className="mr-2 w-100">
							<div>
								<strong>Enabled Menu Items</strong>
							</div>
							<hr className="my-1"/>
							{enabled.map((item,i)=>(
								<>
									{
										i=== 0
										//the dashboard must always be first and cannot be editied
										? <DashboardItem key={item.id}/>
										: item.item_type === 'menu_group'
										? <Group handleEdit={handleEditGroup} key={item.id} group={item}/>
										: <NavItem shadow navItem={item} />
									}
									<DropPreview 
										accept={[NAV_ITEM,GROUP]}
										dropPath={[0,i+1]}
									/>
								</>
							))}
						</div>
						<div
							style={{
								width:'1px',
								background:'#ccc',
								margin:'0 8px',
								flex:'0 0 auto'
							}}
						/>
						<DisabledItems>
							{disabled.map(item=>(
								item.item_type === 'menu_group'
								? <><Group handleEdit={handleEditGroup} group={item}/><Spacer/></>
								: <><NavItem navItem={item} shadow/><Spacer/></>
							))}
						</DisabledItems>
					</div>
				</RootLevel>
			</DndProvider>
			<CreateGroupModal
				show={showCreateGroupModal}
				onHide={setShowCreateGroupModal.bind(null,false)}
			/>
			<EditGroupModal
				show={showEditGroupModal}
				group={editGroup}
				onHide={setShowEditGroupModal.bind(null,false)}
			/>
		</div>
	)
}